import React, { useState } from 'react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

// Accept monthlyData and yearlyData as props
const LoanCustomerChart = ({ monthlyData = [], yearlyData = [] }) => {
  const [isMonthly, setIsMonthly] = useState(true);
  
  // Use the passed props for data
  const data = isMonthly ? (monthlyData || []) : (yearlyData || []);
  // Ensure keys match the data structure from StaffProfile.jsx
  const loanAmountKey = 'totalLoanAmount';
  const customerKey = 'totalCustomers';
  const timeKey = isMonthly ? 'month' : 'year';

  // Custom Tooltip Content Component
  const CustomTooltipContent = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      // The payload array contains entries for each data series (Bar, Line)
      // at the hovered point. We find the specific data by looking at the dataKey.
      const loanEntry = payload.find(p => p.dataKey === loanAmountKey);
      const customerEntry = payload.find(p => p.dataKey === customerKey);

      return (
        <div style={{ backgroundColor: '#2a2a2a', border: '1px solid #444', borderRadius: '4px', padding: '10px', color: '#FFFFFF', fontSize: '12px' }}>
          <p style={{ color: '#FFC107', fontWeight: 'bold', marginBottom: '5px' }}>{label}</p>
          {loanEntry && (
            <p style={{ margin: '0 0 3px 0' }}>
              {loanEntry.name}: {loanEntry.value >= 1000 ? `${(loanEntry.value / 1000).toFixed(1)}k` : loanEntry.value}
            </p>
          )}
          {customerEntry && (
            <p style={{ margin: '0' }}>
              {customerEntry.name}: {customerEntry.value}
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  const handleToggle = (type) => {
    setIsMonthly(type === 'monthly');
  };

  return (
    <div style={{ backgroundColor: '#131313', padding: '20px', borderRadius: '8px', color: '#FFFFFF' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h3 style={{ margin: 0 }}>TOTAL LOAN AMOUNT AND TOTAL CUSTOMERS</h3>
        <div style={{ display: 'inline-flex', borderRadius: '5px', overflow: 'hidden' }}>
          <button
            style={{
              backgroundColor: isMonthly ? '#FFC107' : 'transparent',
              color: isMonthly ? '#000' : '#FFC107',
              border: '1px solid #FFC107',
              padding: '8px 15px',
              cursor: 'pointer',
              borderRight: 'none',
            }}
            onClick={() => handleToggle('monthly')}
          >
            Monthly
          </button>
          <button
            style={{
              backgroundColor: !isMonthly ? '#FFC107' : 'transparent',
              color: !isMonthly ? '#000' : '#FFC107',
              border: '1px solid #FFC107',
              padding: '8px 15px',
              cursor: 'pointer',
              borderLeft: 'none',
            }}
            onClick={() => handleToggle('yearly')}
          >
            Yearly
          </button>
        </div>
      </div>
      {/* Decreased height from 300 to 250 */}
      <ResponsiveContainer width="100%" height={250}> 
        <BarChart data={data} margin={{ top: 5, right: 0, left: -25, bottom: 5 }}> {/* Adjusted left margin for YAxis label visibility */}
          <CartesianGrid strokeDasharray="3 3" stroke="#333" />
          <XAxis dataKey={timeKey} stroke="#FFFFFF" tick={{ fontSize: 10 }} />
          <YAxis 
            yAxisId="left" 
            orientation="left" 
            stroke="#FFFFFF" 
            label={{ value: 'Loan Amount', angle: -90, position: 'insideLeft', fill: '#FFFFFF', dy: 40, fontSize: 12 }} 
            tick={{ fontSize: 10 }}
            tickFormatter={(value) => value >= 1000 ? `${value/1000}k` : value} // Format as 10k, 20k
          />
          <YAxis 
            yAxisId="right" 
            orientation="right" 
            stroke="#FFFFFF" 
            label={{ value: 'No of Customers', angle: 90, position: 'insideRight', fill: '#FFFFFF', dy: -40, fontSize: 12 }} 
            tick={{ fontSize: 10 }}
            tickFormatter={(value) => Number.isInteger(value) ? value : ''} // Show only integer ticks
          />
          <Tooltip content={<CustomTooltipContent />} /> {/* Use custom content */}
          <Legend wrapperStyle={{ color: '#FFFFFF', fontSize: '12px', paddingTop: '10px' }} />
          <Bar yAxisId="left" dataKey={loanAmountKey} fill="#FFC107" name="Loan Amount" barSize={20} />
          <Line 
            yAxisId="right" 
            type="monotone" 
            dataKey={customerKey} 
            stroke="#A020F0" 
            strokeWidth={2} 
            name="Customers" 
            dot={{ r: 3 }} 
            activeDot={{ r: 5 }}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default LoanCustomerChart;